import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useSession } from '../../state/useSession';
import { useBuildRun } from '../../state/buildRun';
import { useDraft } from '../../state/draft';
import { ApiError } from '../../api/client';
import { planApp, type PlanResult } from '../../api/build';
import { creditBalance } from '../../api/billing';
import { scaffoldFor } from '@/webapp/plan/scaffolds';
import { isCollecting, userFeature, type AppPlan } from '@/webapp/plan/planTypes';
import { KITS, kitById } from '@/webapp/runtime/kits';
import { kitOptions, withTopPick } from '@/webapp/runtime/kitOptions';
import { buildCostNote } from '@/billing/buildCostNote';
import { rankKitsFromFeedback } from '@/onboarding/rankKitsFromFeedback';
import KitMockup from '../../components/KitMockup';
import { pickStyleFromPicture } from '@/onboarding/pickStyleFromPicture';
import { generate } from '../../api/llm';
import ContentStep from './ContentStep';
import JourneyDiagram from './JourneyDiagram';
import { Button, Screen } from '../../ui';

type StepId = 'features' | 'content' | 'look' | 'charts' | 'review';

const TITLES: Record<StepId, string> = {
  features: 'What it does',
  content: 'Your own content',
  look: 'How it looks',
  charts: 'What it shows',
  review: 'Ready to build',
};

const SUBTITLES: Record<StepId, string> = {
  features: 'Tap to include or leave out. Add anything missing.',
  content: 'Optional. A text, a picture, a clip — the app is built around it.',
  look: 'Three directions for your app. Tap the one you want.',
  charts: 'Built from your own data, never invented numbers.',
  review: 'Change anything here — nothing is built until you tap Build.',
};

/** What a signed-out visitor is told on the look step — where "Try again" would never work. */
const SIGN_IN_FOR_LOOKS = 'Describing a look, or matching one to a picture, needs a free account. Or pick one of these.';

/** What somebody signed in is told when /llm refuses them: they HAVE an account; it is the session that ended. */
const SESSION_ENDED = 'Your session has ended. Sign in again to describe a look or match a picture — or pick one of these.';

// No 'charts' step: which numbers are worth drawing is a judgement the planner already
// makes, and it is not a decision worth stopping somebody for before they have seen the
// app. They are still planned, still built, still listed on the review step.
const STEPS: StepId[] = ['features', 'content', 'look', 'review'];

/*
 * How long the price waits on /plan before reading the balance anyway.
 *
 * It waits at all because /plan takes its charge BEFORE the model runs, and a balance read
 * sent alongside could come back first. But the charge is taken in the call's first moments;
 * the model is the slow part, and the client lets a plan run for the build Lambda's full ten
 * minutes — so waiting for the answer let a hung call keep the price off the screen for all
 * of that. Past this the charge is already in the balance, and the balance is read again when
 * /plan does settle, since a refund raises it.
 */
const PLAN_CHARGE_TAKEN_MS = 15_000;

/**
 * The server's tailoring of this idea — asked for at most once per idea, whatever the screen
 * does meanwhile.
 *
 * `/plan` charges before it answers and takes 10–40s, and the screen can be left and reopened
 * in that time (browser back or forward). Tied to the mounted screen, the answer was thrown
 * away when it landed on an unmounted one, and the remount paid for it again — or, reopened
 * before it landed, asked a second time alongside it. So the call belongs to the draft: one in
 * flight per idea, shared by every mount, and its answer kept there even if no screen is left
 * to show it.
 */
function tailorPlan(idea: string): Promise<PlanResult> {
  const draft = useDraft.getState();
  if (draft.tailoring?.idea === idea) return draft.tailoring.result;
  const result = Promise.resolve(planApp(idea));
  draft.setTailoring({ idea, result });
  const settle = (plan: AppPlan | null | undefined): void => {
    const now = useDraft.getState();
    if (now.tailoring?.result === result) now.setTailoring(null);
    // Kept while the draft is still about this idea — the only screen that could use it.
    if (plan && now.description === idea) now.setTailored({ idea, plan });
  };
  result.then((r) => settle(r?.plan), () => settle(null));
  return result;
}

/** A picture, read by the browser, as the base64 data URI the vision call takes. */
function readAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('could not read that picture'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  });
}

/**
 * Ported from src/app/(onboarding)/plan.tsx.
 *
 * The plan on screen is a SCAFFOLD rendered synchronously from the idea, so this paints
 * immediately and never shows a spinner over an empty page. The model pass then refines
 * it in place. If that pass fails, times out, or the person is offline, what is already
 * here is a complete, buildable plan — which is why there is no error state.
 */
export default function Plan() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSession();
  const store = useDraft();
  const idea = store.description;

  /*
   * A plan already in the draft is one this person has been looking at — back from signing
   * in, or from signing up. If the server has tailored this idea, that tailoring has been
   * paid for (/plan charges before it answers), so it is not bought again; with no working
   * copy in the draft it is what the screen opens on. Otherwise the tailoring still runs, but
   * lands on a resumed plan as a suggestion: it must never be laid over choices already made.
   */
  const [resumed] = useState(() => ({
    plan: store.plan != null,
    tailored: store.tailored?.idea === idea,
  }));
  const [plan, setPlan] = useState<AppPlan>(
    () => store.plan ?? (store.tailored?.idea === idea ? store.tailored.plan : scaffoldFor(idea)),
  );
  /** Whether /plan has finished — charged or refunded — so the balance read after it is true. */
  const [planSettled, setPlanSettled] = useState(resumed.tailored);
  /** Whether /plan has been out long enough that its charge is in the balance (see PLAN_CHARGE_TAKEN_MS). */
  const [planWaitOver, setPlanWaitOver] = useState(false);
  // Back on the step they left for sign-in, not at the start of the wizard.
  const [step, setStep] = useState(() => Math.max(0, STEPS.indexOf(store.planStep as StepId)));
  const [suggestion, setSuggestion] = useState<AppPlan | null>(null);
  const [refineNotice, setRefineNotice] = useState<string | null>(null);
  /** The notice is /plan's out-of-credits refusal, whose last sentence depends on the balance. */
  const [refusedForCredits, setRefusedForCredits] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const [matching, setMatching] = useState(false);
  /** Under the looks: what happened, and — when the session ended — the way back in. */
  const [styleNotice, setStyleNotice] = useState<{ text: string; signIn?: boolean } | null>(null);
  const picture = useRef<HTMLInputElement>(null);
  // A user edit must never be overwritten by a refinement that was already in flight.
  const dirty = useRef(resumed.plan);

  const steps = STEPS;
  const current = steps[Math.min(step, steps.length - 1)];

  // Read once, above; left in the draft it would reopen a later visit mid-way too.
  useEffect(() => {
    if (useDraft.getState().planStep) useDraft.getState().setPlanStep(null);
  }, []);

  const edit = useCallback((next: (p: AppPlan) => AppPlan) => {
    dirty.current = true;
    setPlan(next);
  }, []);

  useEffect(() => {
    if (resumed.tailored) return;
    let live = true;
    const waited = setTimeout(() => {
      if (live) setPlanWaitOver(true);
    }, PLAN_CHARGE_TAKEN_MS);
    void (async () => {
      try {
        // Kept in the draft by tailorPlan itself, so leaving before it lands loses nothing.
        const { plan: refined } = await tailorPlan(idea);
        if (!live || !refined) return;
        if (dirty.current) setSuggestion(refined);
        else setPlan(refined);
      } catch (err) {
        if (!live) return;
        /*
         * The scaffold on screen is already a complete, buildable plan, so a failed
         * refinement is never fatal and never a redirect. But it is no longer FREE — /plan
         * charges — so silence would leave someone with a quietly worse plan and no idea
         * why. A dismissible line, and Build still works.
         */
        if (err instanceof ApiError && err.kind === 'out_of_credits') {
          /*
           * No promise that the build is affordable: /plan is now refused whenever the balance
           * cannot cover the plan AND the build, so this can mean the build is out of reach too.
           * The price note under the plan says which, from the balance itself.
           */
          setRefineNotice("We couldn't tailor this plan — there aren't enough credits to tailor it and build it.");
          setRefusedForCredits(true);
        } else if (err instanceof ApiError && err.kind === 'unauthorized') {
          setRefineNotice('Sign in and we can tailor this plan to your idea.');
        }
        // Anything else stays silent: the person has a working plan and asked for nothing.
      } finally {
        clearTimeout(waited);
        if (live) setPlanSettled(true);
      }
    })();
    return () => {
      live = false;
      clearTimeout(waited);
    };
    // Runs once per idea: the refinement is of THIS idea, and re-running would fight edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea]);

  /**
   * The ranking on offer and how far through it we are — ONE value, deliberately.
   *
   * Held together rather than as two pieces of state, because on the phone they disagreed:
   * feedback set a new ranking while the round stayed where it was, and the model's best
   * answer was rotated out of view at the moment somebody asked for it.
   */
  const [kitChoice, setKitChoice] = useState<{ ranking: string[] | null; round: number }>(() => ({
    /*
     * Back from signing in, the look they picked may have come from "Show me others" or a
     * picture, and so not be among the plan's first three — and the effect below replaces a
     * selection that is not on offer with the first card. Put at the top, it is on offer.
     */
    ranking: resumed.plan ? withTopPick(plan.kitRanking, plan.kitId) : null,
    round: 0,
  }));
  const [kitFeedback, setKitFeedback] = useState('');
  const [rankingKits, setRankingKits] = useState(false);

  const shownKits = useMemo(
    () => kitOptions(kitChoice.ranking ?? plan.kitRanking, kitChoice.round),
    [kitChoice, plan.kitRanking],
  );

  // Whatever is on screen must be what gets built: the plan carries ONE kitId to the
  // generator, so a selection that scrolls out of the offered three would build a look the
  // person can no longer see.
  useEffect(() => {
    if (!shownKits.includes(plan.kitId)) edit((p) => ({ ...p, kitId: shownKits[0] }));
  }, [shownKits, plan.kitId, edit]);

  /*
   * The look step's two model calls go through /llm, which refuses a caller with no session.
   * The shared code turns every failure into "try again" — right for a network blip, and a
   * dead end for somebody who is not signed in, who can retry forever. So a signed-out
   * visitor is offered the sign-in instead of the calls (below), and a session that lapsed
   * mid-visit is caught here: the refusal is noted on the way through, and said as what it is.
   */
  function lookCall() {
    const refused = { sessionEnded: false };
    const call: typeof generate = async (input) => {
      try {
        return await generate(input);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'unauthorized') refused.sessionEnded = true;
        throw err;
      }
    };
    return { refused, generate: call };
  }

  /**
   * To sign in and come back to THIS plan, on this step: the plan (look included) and the
   * step are kept in the draft, and `from` rides through sign-up and confirmation too — the
   * invitation says "free account", so most who take it have none yet.
   */
  function signInForLooks(): void {
    store.setPlan(plan);
    store.setAppName(plan.appName);
    store.setPlanStep(current);
    navigate('/sign-in', { state: { from: '/new/plan' } });
  }

  async function applyKitFeedback(): Promise<void> {
    if (rankingKits) return;
    setRankingKits(true);
    setStyleNotice(null);
    try {
      const call = lookCall();
      const res = await rankKitsFromFeedback(idea, kitFeedback, isAuthenticated, { generate: call.generate });
      if (res.kind === 'failed') {
        setStyleNotice(call.refused.sessionEnded ? { text: SESSION_ENDED, signIn: true } : { text: res.reason });
        return;
      }
      setKitChoice({ ranking: res.kitRanking, round: 0 });
      // The new best answer becomes the selected one: somebody who typed "warmer" has just
      // said the current choice is not what they want.
      edit((p) => ({ ...p, kitId: kitOptions(res.kitRanking, 0)[0] }));
      setKitFeedback('');
    } finally {
      setRankingKits(false);
    }
  }

  /*
   * The balance. An unanswered call leaves everything undefined, and `buildCostNote` stays
   * silent on undefined rather than guessing — a price we are not sure of is worse than no
   * price, because somebody acts on it.
   *
   * Read AFTER /plan has settled, never alongside it. Once an account has built, /plan
   * charges 500 before it answers, and a balance read in parallel could come back first:
   * 1,200 on screen, "you can afford it", 700 left and a refused build. Read again each
   * time the review step is reached, because the look step's own calls spend too, and a
   * number from before them is no longer the one the build will meet.
   */
  const [credits, setCredits] = useState<{
    tier?: 'free' | 'paid' | 'unlimited';
    creditsRemaining?: number;
    firstBuildDone?: boolean;
  }>({});
  const onReview = current === 'review';
  useEffect(() => {
    if (!onReview) {
      // Left the review, so what it said is about to be stale: back on it, the note stays
      // silent until the new read lands rather than showing the old number first.
      setCredits((c) => (Object.keys(c).length ? {} : c));
      return;
    }
    if (!isAuthenticated || !(planSettled || planWaitOver)) return;
    let live = true;
    void creditBalance()
      .then((b) => {
        if (!live) return;
        setCredits({
          tier: b.tier as 'free' | 'paid' | 'unlimited',
          creditsRemaining: b.creditsRemaining,
          firstBuildDone: b.firstBuildDone,
        });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [isAuthenticated, planSettled, planWaitOver, onReview]);

  /**
   * What this build will cost, worked out before the button rather than after it.
   *
   * This screen used to say nothing at all. An anonymous visitor walked six steps, pressed
   * "Build this app", and met a Create account screen — never told the price, never told an
   * account was needed. The sentence is shared with the phone so the two cannot drift.
   */
  const costNote = buildCostNote({
    signedIn: isAuthenticated,
    tier: credits.tier,
    creditsRemaining: credits.creditsRemaining,
    firstBuildDone: credits.firstBuildDone,
  });

  function build(): void {
    store.setPlan(plan);
    store.setAppName(plan.appName);
    if (!isAuthenticated) {
      store.setPendingIntent('build');
      // Back to this review after the account exists, rather than to the home page.
      store.setPlanStep(current);
      navigate('/sign-up', { state: { from: '/new/plan' } });
      return;
    }
    void useBuildRun.getState().start(user?.id ?? '');
  }

  /**
   * Turn a reference picture into a look.
   *
   * The decisions are the shared ones — `pickStyleFromPicture` takes injectable `pick` and
   * `generate`, so the browser supplies a file input and its own proxy call, and everything
   * that JUDGES anything (parsing the reply, choosing the nearest kit, refusing a reply it
   * cannot read) stays identical to the phone's.
   *
   * A picture that gives no clear look leaves the plan EXACTLY as it was and says so. A
   * half-applied style — a kit that changed for no visible reason — is worse than none.
   */
  async function matchPicture(file: File): Promise<void> {
    setMatching(true);
    setStyleNotice(null);
    try {
      const call = lookCall();
      const result = await pickStyleFromPicture(idea, isAuthenticated, {
        pick: () => readAsDataUri(file),
        generate: call.generate,
      });
      // Cancelling is not a failure and must not be reported as one.
      if (!result) return;
      if (result.kind === 'failed') {
        setStyleNotice(call.refused.sessionEnded ? { text: SESSION_ENDED, signIn: true } : { text: result.reason });
        return;
      }
      /*
       * The nearest kit can be any of the eight, not only the three on screen, so it goes to
       * the FRONT of the ranking as well. Setting the kit alone let the effect above swap an
       * unoffered kit straight back to shownKits[0] — the notice below then named a look that
       * was neither ticked nor built. The same shared rule as the phone's.
       */
      setKitChoice((c) => ({ ranking: withTopPick(c.ranking ?? plan.kitRanking, result.kitId), round: 0 }));
      edit((p) => ({ ...p, kitId: result.kitId }));
      store.setStyleBrief(result.styleBrief);
      setStyleNotice({ text: `Using ${KITS.find((k) => k.id === result.kitId)?.label ?? result.kitId}, matched to your picture.` });
    } finally {
      setMatching(false);
      // So the same picture can be chosen again.
      if (picture.current) picture.current.value = '';
    }
  }

  const addFeature = (): void => {
    const label = newFeature.trim();
    if (!label) return;
    edit((p) => ({ ...p, features: [...p.features, userFeature(label, p.features)] }));
    setNewFeature('');
  };

  return (
    <Screen title={TITLES[current]}>
      <ol className="plan-progress" aria-label="Progress">
        {steps.map((s, i) => (
          <li key={s} className={i <= step ? 'plan-dot is-done' : 'plan-dot'} aria-current={i === step ? 'step' : undefined}>
            <span className="visually-hidden">{TITLES[s]}</span>
          </li>
        ))}
      </ol>
      <p className="type-body plan-sub">{SUBTITLES[current]}</p>

      {refineNotice && (
        <p role="status" className="type-caption plan-detail plan-notice">
          {refineNotice}{' '}
          {/*
            "The plan below still works as it is" — said to a free account that had built its one
            app, while the note under the plan said the build needs credits they do not have. Said
            now only when the balance is KNOWN to cover the build: not when it is short, and not
            before it has been read (which is the review step), because that would be a guess.
          */}
          {refusedForCredits && (costNote.tone === 'cost' || costNote.tone === 'free') && (
            <>The plan below still works as it is.{' '}</>
          )}
          <button type="button" className="chip-button" onClick={() => { setRefineNotice(null); setRefusedForCredits(false); }}>Dismiss</button>
        </p>
      )}

      {suggestion && (
        <div className="plan-suggestion card clay-card" role="status">
          <p className="type-body">We refined this plan while you were editing.</p>
          <Button
            title="Use the refined plan"
            variant="secondary"
            onPress={() => {
              setPlan(suggestion);
              // Its own ranking, not the one kept for the plan it replaces.
              setKitChoice({ ranking: null, round: 0 });
              setSuggestion(null);
            }}
          />
          <Button title="Keep mine" variant="ghost" onPress={() => setSuggestion(null)} />
        </div>
      )}

      {current === 'features' && (
        <>
          <ul className="plan-list">
            {plan.features.map((f) => (
              <li key={f.id} className="card clay-card plan-item">
                <label className="plan-check">
                  <input
                    type="checkbox"
                    checked={f.included}
                    onChange={() =>
                      edit((p) => ({
                        ...p,
                        features: p.features.map((x) => (x.id === f.id ? { ...x, included: !x.included } : x)),
                      }))
                    }
                  />
                  <span>
                    <span className="type-heading">{f.label}</span>
                    <span className="type-caption plan-detail">{f.detail}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="plan-add">
            <label className="type-caption field-label" htmlFor="new-feature">Add something missing</label>
            <input
              id="new-feature"
              className="field"
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addFeature();
                }
              }}
            />
            <Button title="Add" variant="secondary" onPress={addFeature} disabled={!newFeature.trim()} />
          </div>
        </>
      )}

      {current === 'content' && <ContentStep />}

      {current === 'look' && (
        <>
          <label className="type-caption field-label" htmlFor="app-name">Name</label>
          <input
            id="app-name"
            className="field"
            value={plan.appName}
            onChange={(e) => edit((p) => ({ ...p, appName: e.target.value }))}
          />
          <label className="type-caption field-label" htmlFor="tagline">Tagline</label>
          <input
            id="tagline"
            className="field"
            value={plan.tagline}
            onChange={(e) => edit((p) => ({ ...p, tagline: e.target.value }))}
          />
          {/*
            * Three directions, each wearing this person's own app.
            *
            * The browser gets the kit's font stack exactly as written, so the mockup uses the
            * same faces the built page will. What is sketched is the arrangement, which the
            * generator decides; what is exact is the identity, which is what is being chosen.
            */}
          <div role="radiogroup" aria-label="How it looks">
            {shownKits.map((id) => {
              const kit = kitById(id);
              if (!kit) return null;
              const selected = plan.kitId === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`look-card pressable ${selected ? 'is-selected' : ''}`}
                  onClick={() => edit((p) => ({ ...p, kitId: id }))}
                >
                  <span className="look-head">
                    <span className="type-heading">{kit.label}</span>
                    {selected && <span aria-hidden="true">✓</span>}
                  </span>
                  <span className="type-caption plan-detail">{kit.blurb}</span>
                  <span className="look-strip">
                    <KitMockup kit={kit} plan={plan} width={100} />
                  </span>
                </button>
              );
            })}
          </div>

          <input
            ref={picture}
            id="style-picture"
            type="file"
            accept="image/*"
            className="visually-hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void matchPicture(file);
            }}
          />
          <div className="look-actions">
            <Button
              title="Show me others"
              variant="secondary"
              onPress={() => setKitChoice((c) => ({ ...c, round: c.round + 1 }))}
              accessibilityLabel="Show me three other looks"
            />
            {isAuthenticated && (
              <Button
                title={matching ? 'Reading…' : 'Match a picture'}
                variant="secondary"
                loading={matching}
                onPress={() => picture.current?.click()}
                accessibilityLabel="Choose the look from a picture"
              />
            )}
          </div>

          {/* Say what you want instead. Kits are a closed set the compile gate enforces, so
              this re-ranks the eight rather than inventing a ninth. Both this and the
              picture are model calls, which need an account: signed out, the invitation
              stands where they would, and the three kits and "Show me others" still work. */}
          {!isAuthenticated ? (
            <p className="type-caption plan-detail">
              {SIGN_IN_FOR_LOOKS}{' '}
              <button type="button" className="chip-button" onClick={signInForLooks}>Sign in</button>
            </p>
          ) : (
            <div className="look-feedback">
              <input
                className="field"
                value={kitFeedback}
                onChange={(e) => setKitFeedback(e.target.value)}
                placeholder="Warmer? Bolder? Less corporate?"
                disabled={rankingKits}
                aria-label="Say what you would like the look to be"
                onKeyDown={(e) => { if (e.key === 'Enter') void applyKitFeedback(); }}
              />
              <Button
                title={rankingKits ? '…' : 'Go'}
                loading={rankingKits}
                disabled={!kitFeedback.trim()}
                onPress={() => void applyKitFeedback()}
                accessibilityLabel="Show looks matching that"
              />
            </div>
          )}

          {styleNotice && (
            <p role="status" className="type-caption plan-detail">
              {styleNotice.text}
              {styleNotice.signIn && (
                <>
                  {' '}
                  <button type="button" className="chip-button" onClick={signInForLooks}>Sign in</button>
                </>
              )}
            </p>
          )}
        </>
      )}

      {current === 'charts' && (
        <ul className="plan-list">
          {plan.charts.length === 0 && <li className="type-body">No charts for this one.</li>}
          {plan.charts.map((c) => (
            <li key={c.id} className="card clay-card plan-item">
              <label className="plan-check">
                <input
                  type="checkbox"
                  checked={c.included}
                  onChange={() =>
                    edit((p) => ({
                      ...p,
                      charts: p.charts.map((x) => (x.id === c.id ? { ...x, included: !x.included } : x)),
                    }))
                  }
                />
                <span>
                  <span className="type-heading">{c.label}</span>
                  <span className="type-caption plan-detail">{c.source}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {current === 'review' && (
        <div className="plan-review">
          <h2 className="type-title">{plan.appName}</h2>
          <p className="type-body plan-detail">{plan.tagline}</p>

          {/* The journey, before the inventory: "is this the app I asked for?" is a question
              about what using it feels like, not about which rows exist. */}
          <h3 className="type-micro section-label">What someone goes through</h3>
          <JourneyDiagram plan={plan} />

          <h3 className="type-micro section-label">It will</h3>
          <ul className="plan-review-list">
            {plan.features.filter((f) => f.included).map((f) => (
              <li key={f.id} className="type-body">{f.label}</li>
            ))}
          </ul>
          {plan.constraints.length > 0 && (
            <>
              <h3 className="type-micro section-label">Worth knowing</h3>
              <ul className="plan-review-list">
                {plan.constraints.map((c) => (
                  <li key={c.id} className="type-body">
                    {c.label}
                    {c.workaround && <span className="type-caption plan-detail">{c.workaround}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {/* The price, before the button. This screen said nothing at all: an anonymous
              visitor walked six steps, pressed Build, and met a Create account screen —
              never told the cost, never told an account was needed. */}
          {costNote.tone !== 'silent' && (
            <div className={`cost-note tone-${costNote.tone}`} role="note">
              <span className="type-body cost-headline">{costNote.headline}</span>
              <span className="type-caption cost-detail">{costNote.detail}</span>
              {/* "Top up to build it", with nowhere on the screen to top up. The way past the
                  note is one tap away, from the note itself. */}
              {costNote.tone === 'short' && (
                <Link to="/credits" className="type-caption cost-link">Get credits</Link>
              )}
            </div>
          )}
        </div>
      )}

      <div className="plan-actions">
        {step > 0 && <Button title="Back" variant="ghost" onPress={() => setStep((s) => s - 1)} />}
        {current === 'review' ? (
          <Button title="Build it" onPress={build} accessibilityLabel="Build this app" />
        ) : (
          <Button title="Next" onPress={() => setStep((s) => Math.min(s + 1, steps.length - 1))} />
        )}
      </div>
    </Screen>
  );
}
